import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Bell, Mail, Megaphone, Smartphone, ArrowRight, Check } from "lucide-react";

export default function EventMarketing() {
    const canonicalUrl = "https://eventsbilletterie.com/communication-evenementielle";
    const seoTitle = "Communication événementielle et promotion d'événements | Events billetterie";
    const seoDescription =
        "Boostez la visibilité de vos événements avec Events billetterie : notifications push, campagnes e-mail, relais réseaux sociaux et exposition sur le site et l'application mobile.";
    const seoKeywords =
        "communication evenementielle, promotion événement, notification push événement, campagne email événement, reseaux sociaux événement, visibilité événement cote d'ivoire, billetterie en ligne cote d'ivoire, vente de tickets événement, gestion inscriptions, controle d'acces qr code, billets physiques événement, bracelets unique, bracelets securises, ticket unique, ticket unique cote d'ivoire, ticket securise, ticket infalsifiable, ticket qr code, bracelet qr code, qr code unique";
    const seoImage = "https://eventsbilletterie.com/assets/images/facadeEvents.png";

    const channels = [
        {
            icon: Bell,
            title: "Notification push ciblée",
            description:
                "Touchez rapidement les utilisateurs actifs de l'application avec des notifications push autour de votre événement.",
            points: [
                "Annonce de lancement de la billetterie",
                "Relance avant les dates clés",
                "Rappel utile proche de l'événement",
            ],
        },
        {
            icon: Mail,
            title: "Campagnes e-mail à large réseau",
            description:
                "Diffusez votre événement via des campagnes e-mailing pour augmenter la portée et accélérer les ventes.",
            points: [
                "Mise en avant de l'événement",
                "Message adapté à votre cible",
                "Canal performant pour convertir",
            ],
        },
        {
            icon: Megaphone,
            title: "Relais sur nos réseaux sociaux",
            description:
                "Gagnez en crédibilité et en visibilité grâce aux publications et relais sur les réseaux sociaux Events.",
            points: [
                "Exposition à une audience qualifiée",
                "Renforcement de votre image",
                "Effet de répétition sur les canaux",
            ],
        },
        {
            icon: Smartphone,
            title: "Visibilité native site + application",
            description:
                "Le fait d'être présent sur le site officiel et l'application Events est déjà un puissant outil de communication.",
            points: [
                "Découverte organique de votre événement",
                "Présence continue sur deux canaux",
                "Parcours direct jusqu'à l'achat",
            ],
        },
    ];

    const eventMarketingSeoJsonLd = {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Communication evenementielle",
        name: seoTitle,
        description: seoDescription,
        url: canonicalUrl,
        inLanguage: "fr-CI",
        provider: {
            "@type": "Organization",
            name: "Events Billetterie",
            url: "https://eventsbilletterie.com",
        },
        areaServed: {
            "@type": "Country",
            name: "Cote d'Ivoire",
        },
    };

    return (
        <main className="nc-PageAbout overflow-hidden">
            <Helmet>
                <html lang="fr" />
                <title>{seoTitle}</title>
                <meta name="description" content={seoDescription} />
                <meta name="keywords" content={seoKeywords} />
                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
                />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="fr_CI" />
                <meta property="og:site_name" content="Events Billetterie" />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={seoImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoTitle} />
                <meta name="twitter:description" content={seoDescription} />
                <meta name="twitter:image" content={seoImage} />
                <meta name="twitter:site" content="@events_billetterie" />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json">{JSON.stringify(eventMarketingSeoJsonLd)}</script>
            </Helmet>

            <div className="container max-w-6xl py-14 md:py-20 px-4">
                <section className="max-w-4xl mx-auto text-center">
                    <span className="inline-flex items-center rounded-full bg-primary-6000/10 text-primary-700 dark:text-primary-400 px-4 py-1.5 text-sm font-medium">
                        Communication événementielle
                    </span>
                    <h1 className="mt-5 text-4xl md:text-5xl font-bold text-neutral-900 dark:text-neutral-100">
                        Faites rayonner vos événements sur plusieurs canaux
                    </h1>
                    <p className="mt-5 text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
                        Avec Events billetterie, votre communication ne se limite pas à une publication unique. Vous combinez
                        notifications push, campagnes e-mail, réseaux sociaux et visibilité permanente sur notre site et
                        notre application.
                    </p>
                </section>

                <section className="mt-12 grid sm:grid-cols-2 gap-6">
                    {channels.map(({ icon: Icon, title, description, points }) => (
                        <article
                            key={title}
                            className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-6 shadow-sm"
                        >
                            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gray-600/10 text-gray-600 mb-4">
                                <Icon className="w-5 h-5" aria-hidden />
                            </div>
                            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">{title}</h2>
                            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">{description}</p>
                            <ul className="mt-4 space-y-2">
                                {points.map((item) => (
                                    <li
                                        key={item}
                                        className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300"
                                    >
                                        <Check className="w-4 h-4 text-primary-6000 mt-0.5 shrink-0" aria-hidden />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </section>

                <section className="mt-12 rounded-3xl bg-primary-6000 text-white px-6 py-10 md:px-10 md:py-12 text-center">
                    <h2 className="text-2xl md:text-5xl font-bold">
                    Prêt à vous lancer ?
                    </h2>
                    <p className="mt-3 text-white/90 max-w-3xl mx-auto">
                        Chaque levier renforce l&apos;autre : la découverte sur le site et l&apos;application, la relance
                        push, le relais social et l&apos;e-mailing créent un effet cumulé qui soutient vos ventes.
                    </p>
                    <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            to="/nous-contacter"
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-primary-6000 font-semibold px-7 py-2.5 text-sm hover:bg-neutral-100 transition-colors"
                        >
                            Parler de mon événement
                            <ArrowRight className="w-4 h-4" aria-hidden />
                        </Link>
                        <Link
                            to="/offre-billetterie"
                            className="inline-flex items-center justify-center rounded-full text-white font-semibold px-7 py-2.5 text-sm hover:bg-white/10 transition-colors"
                        >
                            Voir l&apos;offre de billetterie
                        </Link>
                    </div>
                </section>
            </div>
        </main>
    );
}
